import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { RoutingRule, RoutingRuleInput } from '@/api/adminApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ActiveBadge, FormError, ListState, RowActions, tdClass, thClass } from '@/components/admin/AdminShared'
import { useAdminResource } from '@/hooks/useAdmin'
import { EMPTY_RULE_FORM, formToRule, ruleToForm, validateRule, type RoutingRuleForm } from '@/lib/adminForms'

const compact = (v: unknown) => JSON.stringify(v ?? {})

export function RoutingTab() {
  const { t } = useTranslation()
  const { list, save, remove } = useAdminResource('routingRules', true)
  const [editing, setEditing] = useState<RoutingRule | 'new' | null>(null)
  const [deleting, setDeleting] = useState<RoutingRule | null>(null)
  const rules = [...(list.data ?? [])].sort((a, b) => a.order - b.order)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>{t('admin.add.routing')}</Button>
      </div>
      <ListState query={list}>
        {rules.length === 0 ? (
          <p className="py-6 text-meta text-ink-muted">{t('admin.empty')}</p>
        ) : (
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-surface-subtle">
              <tr>
                <th className={`${thClass} w-16`}>{t('admin.rule.order')}</th>
                <th className={`${thClass} w-[220px]`}>{t('admin.rule.name')}</th>
                <th className={thClass}>{t('admin.rule.conditions')}</th>
                <th className={thClass}>{t('admin.rule.actions')}</th>
                <th className={`${thClass} w-24`} />
                <th className={`${thClass} w-32`} />
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b border-line">
                  <td className={`${tdClass} font-mono`}>{r.order}</td>
                  <td className={`${tdClass} font-medium text-ink-strong`}>{r.name}</td>
                  <td className={`${tdClass} break-words font-mono text-label`}>{compact(r.conditions)}</td>
                  <td className={`${tdClass} break-words font-mono text-label`}>{compact(r.actions)}</td>
                  <td className={tdClass}>
                    <ActiveBadge active={r.active} />
                  </td>
                  <td className={tdClass}>
                    <RowActions onEdit={() => setEditing(r)} onDelete={() => setDeleting(r)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListState>

      {editing ? (
        <RuleDialog
          rule={editing === 'new' ? null : editing}
          pending={save.isPending}
          error={save.error}
          onClose={() => {
            setEditing(null)
            save.reset()
          }}
          onSave={(input) => save.mutate({ id: editing === 'new' ? undefined : editing.id, input }, { onSuccess: () => setEditing(null) })}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title={t('admin.deleteTitle', { name: deleting?.name ?? '' })}
        body={t('admin.deleteBody')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      />
    </div>
  )
}

function RuleDialog({
  rule,
  pending,
  error,
  onClose,
  onSave,
}: {
  rule: RoutingRule | null
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (input: RoutingRuleInput) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<RoutingRuleForm>(() => (rule ? ruleToForm(rule) : EMPTY_RULE_FORM))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const found = validateRule(form) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) onSave(formToRule(form))
  }

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule ? t('admin.editTitle.routing') : t('admin.createTitle.routing')}</DialogTitle>
        </DialogHeader>
        <form className="grid grid-cols-[1fr_120px] gap-4" onSubmit={onSubmit} noValidate>
          <Field id="rr-name" label={t('admin.rule.name')} error={err('name')}>
            <Input id="rr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field id="rr-order" label={t('admin.rule.order')} error={err('order')}>
            <Input id="rr-order" inputMode="numeric" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          </Field>
          <Field id="rr-cond" label={t('admin.rule.conditions')} error={err('conditions')} className="col-span-2">
            <Textarea id="rr-cond" rows={6} className="font-mono" value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} />
          </Field>
          <Field id="rr-act" label={t('admin.rule.actions')} error={err('actions')} className="col-span-2">
            <Textarea id="rr-act" rows={4} className="font-mono" value={form.actions} onChange={(e) => setForm({ ...form, actions: e.target.value })} />
          </Field>
          <label className="col-span-2 flex items-center gap-2 text-meta text-ink">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            {t('admin.active')}
          </label>
          <FormError error={error} />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
